/**
 * Helpers for rendering referral agreement data as ContractInfo info-sections.
 */

import React from 'react';

const formatCurrency = (val) => {
  if (val == null) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  } catch {
    return `$${val}`;
  }
};

const formatPercent = (val) => {
  if (val == null) return null;
  const n = Number(val);
  if (n <= 1) {
    return `${(n * 100).toFixed(2).replace(/\.?0+$/, '')}%`;
  }
  return `${n}%`;
};

export function hasReferralDetailContent(referralDetails) {
  const fields = buildReferralDetailFields(referralDetails);
  if (!fields) {
    return false;
  }
  return fields.some(([, value]) => value != null && value !== '');
}

export function buildReferralDetailFields(referralDetails) {
  if (!referralDetails) {
    return null;
  }

  const feeType = referralDetails.referral_fee_type;
  const feeAmount = referralDetails.referral_fee_amount;
  const feePercent = referralDetails.referral_fee_percent;
  const feeDisplay =
    feeType === 'amount' && feeAmount != null
      ? formatCurrency(feeAmount)
      : feeType === 'percent' && feePercent != null
        ? formatPercent(feePercent)
        : feeAmount != null
          ? formatCurrency(feeAmount)
          : feePercent != null
            ? formatPercent(feePercent)
            : null;

  return [
    ['Referring Agent', referralDetails.referring_agent_name],
    ['Referring Email', referralDetails.referring_agent_email],
    ['Referring Phone', referralDetails.referring_agent_phone],
    ['Referring Brokerage', referralDetails.referring_brokerage_name],
    ['Brokerage Address', referralDetails.referring_brokerage_address],
    ['Receiving Agent', referralDetails.receiving_agent_name],
    ['Receiving Email', referralDetails.receiving_agent_email],
    ['Receiving Phone', referralDetails.receiving_agent_phone],
    ['Receiving Brokerage', referralDetails.receiving_brokerage_name],
    [
      'Referral Type',
      referralDetails.is_internal_referral == null
        ? null
        : referralDetails.is_internal_referral
          ? 'Internal (same brokerage)'
          : 'External',
    ],
    ['Referral Fee', feeDisplay],
    ['W-9 Received', referralDetails.w9_received == null ? null : referralDetails.w9_received ? 'Yes' : 'No'],
  ];
}

export function buildStandaloneReferralFields(referralDetails) {
  if (!referralDetails) return [];
  const fee = referralDetails.expected_referral_fee_amount ?? referralDetails.referral_fee_amount;
  return [
    ['Property Address', referralDetails.property_address],
    ['City', referralDetails.property_address_city],
    ['State', referralDetails.property_address_state],
    ['Zip', referralDetails.property_address_zip_code],
    ['County', referralDetails.property_address_county],
    ['Purchase Price', referralDetails.purchase_price != null ? formatCurrency(referralDetails.purchase_price) : null],
    ['Expected Referral Fee', fee != null ? formatCurrency(fee) : null],
    ['Closing Date', referralDetails.close_of_escrow_due_date],
    ['Contract Acceptance', referralDetails.contract_acceptance_date],
    ['SkySlope Status', referralDetails.skyslope_sync_status],
  ];
}

/** Plain info-section block for views without ContractInfo.renderSection. */
export function ReferralInfoSection({ referralDetails }) {
  const fields = buildReferralDetailFields(referralDetails);
  if (!fields) {
    return null;
  }

  const visibleFields = fields.filter(([, value]) => value != null && value !== '');
  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className="info-section">
      <h3>Referral Details</h3>
      <div className="info-grid">
        {visibleFields.map(([label, value]) => (
          <div key={label} className="info-item">
            <span className="info-label">{label}:</span>
            <span className="info-value">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
